/** Valore mostrato al centro / tooltip: allineato a Formatter (EUR, 2 decimali) quando format === 'currency'. */
const formatPieValue = (format, locale, val) => {
    const n = typeof val === 'number' ? val : parseFloat(val, 10);
    if (format !== 'currency' || Number.isNaN(n)) {
        return val;
    }
    try {
        return new Intl.NumberFormat(locale || undefined, {
            style: 'currency',
            currency: 'EUR',
            maximumFractionDigits: 2,
        }).format(n);
    } catch {
        return String(val);
    }
};

/** Ritorna { series, options } o null. Un solo oggetto derivato evita doppio setState e doppio update Apex (flicker tooltip). */
export const buildPieChartConfig = (items, monochrome, showLegend, widths, width, height, mode, palette, showTotal, showName, showValues, labelToColorMap, t, handleFilterChange, legendPosition, format, locale, showSliceTooltip) => {
    if (!(items && typeof items === 'object' && !Array.isArray(items))) {
        return null;
    }

    const total = Array.isArray(items.series)
        ? items.series.reduce((a, b) => a + b, 0)
        : 0;

    const hasData = total > 0;
    const monochromel = hasData ? monochrome : {
        enabled: true,
        color: '#CCCCCC',
        shadeTo: 'dark',
        shadeIntensity: 1
    };

    const series = hasData ? items.series : [1];

    const options = {
            labels: hasData ? items.labels : [t('Nessun dato')],
            legend: {
                show: showLegend,
                position: legendPosition,
                width: widths.legend,
                height: 'auto',
                maxHeight: height,
                offsetY: 0,
                floating: false,
                containerMargin: {
                    left: 5,
                    top: 5
                },
                itemMargin: {
                    horizontal: 5,
                    vertical: 2
                },
                markers: {
                    width: 12,
                    height: 12,
                    radius: 2
                },
                labels: {
                    useSeriesColors: true
                },
                formatter: function (seriesName, opts) {
                    return seriesName;
                },
                onItemClick: {
                    toggleDataSeries: false
                }
            },
            theme: {
                mode: mode,
                palette: palette,
                monochrome: monochromel
            },
            chart: {
                type: 'donut',
                width: widths.donut,
                animations: {
                    enabled: false,
                    dynamicAnimation: { enabled: false },
                },
                events: {
                    dataPointSelection: (event, chartContext, config) => {
                        const clickedSliceIndex = config.dataPointIndex;
                        const clickedLabel = config.w.config.labels[clickedSliceIndex];
                        if (handleFilterChange && clickedSliceIndex < 10 && hasData) {
                            setTimeout(() => handleFilterChange(clickedLabel), 100)
                        }
                    }
                }
            },
            responsive: [
                {
                    breakpoint: 600,
                    options: {
                        chart: {
                            width: parseInt(width * 0.75),
                            height: parseInt(height * 0.75),
                        },
                        plotOptions: {
                            pie: {
                                expandOnClick: false,
                                donut: {
                                    labels: {
                                        show: true,
                                        total: {
                                            show: false,
                                        }
                                    }
                                }
                            }
                        },
                        legend: {
                            position: 'bottom',
                            width: undefined,
                            height: undefined
                        }
                    }
                },
                {
                    breakpoint: 0,
                    options: {
                        chart: {
                            width: width,
                            height: width,
                        }
                    }
                }
            ],
            dataLabels: {
                enabled: true,
                enabledOnSeries: undefined,
                formatter: function (val, opts) {
                    return parseFloat(val).toFixed(1) + '%'
                },
                textAnchor: 'middle',
                distributed: false,
                offsetX: 0,
                offsetY: 0,
                style: {
                    fontSize: '10px',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 'bold',
                    colors: ['#FFF'],
                    cursor: 'pointer',
                },
                dropShadow: {
                    enabled: false,
                }
            },
            plotOptions: {
                pie: {
                    expandOnClick: false,
                    states: {
                        hover: { filter: { type: 'none' } },
                        active: { filter: { type: 'none' } },
                    },
                    donut: {
                        size: '50%',
                        labels: {
                            show: true,
                            total: {
                                show: showTotal,
                                fontSize: '12px',
                                fontFamily: 'Helvetica, Arial, sans-serif',
                                label: t('Total'),
                                fontWeight: 200,
                                offsetY: width == 200 ? -20 : -15,
                                color: undefined,
                                formatter: function () {
                                    if (!hasData) {
                                        return format === 'currency' ? formatPieValue(format, locale, 0) : '0';
                                    }
                                    return formatPieValue(format, locale, total);
                                }
                            },
                            name: {
                                show: showName,
                                fontSize: '12px',
                                fontFamily: 'Helvetica, Arial, sans-serif',
                                label: t('Total'),
                                fontWeight: 200,
                                offsetY: width == 200 ? -20 : -15,
                                color: undefined,
                                formatter: function (value) {
                                    return value;
                                }
                            },
                            value: {
                                show: showValues,
                                fontSize: width == 200 ? '30px' : '24px',
                                fontFamily: 'Helvetica, Arial, sans-serif',
                                fontWeight: 600,
                                offsetY: -5,
                                formatter: function (value) {
                                    if (!hasData) return '';
                                    return formatPieValue(format, locale, value);
                                }
                            },
                        }
                    }
                }
            },
            tooltip: {
                enabled: showSliceTooltip !== false,
                theme: 'light',
                shared: false,
                followCursor: false,
                intersect: false,
                y: {
                    formatter: (value) => formatPieValue(format, locale, value),
                },
            }
        };

    if (labelToColorMap) {
        options.colors = items.labels.map(label => {
            return labelToColorMap[label.toLowerCase()] || undefined;
        });
    }

    return { series, options };
}

export const calculateWidths = (width, legendWidth, donutWidth) => {
    // PALETTO: Lavoriamo SOLO con numeri interi (pixel)
    // Se vuoi percentuali, devi convertirle prima di chiamare questa funzione
    
    const hasWidth = typeof width === 'number';
    const hasLegend = typeof legendWidth === 'number';
    const hasDonut = typeof donutWidth === 'number';

    // Caso 1: solo width
    if (hasWidth && !hasLegend && !hasDonut) {
        return {
            donut: Math.floor(width * 0.7),
            legend: Math.floor(width * 0.3)
        };
    }

    // Caso 2: solo legend
    if (!hasWidth && hasLegend && !hasDonut) {
        const totalWidth = Math.floor(legendWidth / 0.3);
        return {
            donut: Math.floor(totalWidth * 0.7),
            legend: legendWidth
        };
    }

    // Caso 3: solo donut
    if (!hasWidth && !hasLegend && hasDonut) {
        const totalWidth = Math.floor(donutWidth / 0.7);
        return {
            donut: donutWidth,
            legend: Math.floor(totalWidth * 0.3)
        };
    }

    // Caso 4: width + legend
    if (hasWidth && hasLegend && !hasDonut) {
        return {
            donut: width - legendWidth,
            legend: legendWidth
        };
    }

    // Caso 5: width + donut
    if (hasWidth && !hasLegend && hasDonut) {
        return {
            donut: donutWidth,
            legend: width - donutWidth
        };
    }

    // Caso 6: legend + donut (senza width)
    if (!hasWidth && hasLegend && hasDonut) {
        return {
            donut: donutWidth,
            legend: legendWidth
        };
    }

    // Caso 7: tutti e tre - verifica coerenza
    if (hasWidth && hasLegend && hasDonut) {
        if (Math.abs((donutWidth + legendWidth) - width) > 1) {
            console.warn(`Width mismatch: donut(${donutWidth}) + legend(${legendWidth}) ≠ total(${width})`);
        }
        return { donut: donutWidth, legend: legendWidth };
    }

    // Default fallback
    return {
        donut: 280,
        legend: 120
    };
};

export const getStyles = (height) => {
    return {
        '& .apexcharts-legend': {
            maxHeight: `${height}px !important`,
            overflowY: 'auto !important',
            overflowX: 'hidden !important',
            padding: '0 5px !important'
        },
        '& .apexcharts-legend-series': {
            display: 'flex !important',
            alignItems: 'center !important',
            marginBottom: '4px !important'
        },
        '& .apexcharts-legend-text': {
            whiteSpace: 'normal !important',
            wordWrap: 'break-word !important',
            lineHeight: '1.4 !important',
            maxWidth: '150px !important',
            display: 'inline-block !important'
        }
    }
}